import React, { useEffect, useState, useRef } from 'react';
import { Globe, Check, Search, ChevronDown } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { Language } from '../types';
const LANGUAGE_FLAGS: Record<Language, string> = {
  en: '🇺🇸',
  id: '🇮🇩',
  ms: '🇲🇾',
  th: '🇹🇭',
  vi: '🇻🇳',
  fil: '🇵🇭',
  km: '🇰🇭',
  lo: '🇱🇦',
  my: '🇲🇲'
};
const LANGUAGE_NAMES: Record<Language, {
  native: string;
  english: string;
}> = {
  en: {
    native: 'English',
    english: 'English'
  },
  id: {
    native: 'Indonesia',
    english: 'Indonesian'
  },
  ms: {
    native: 'Melayu',
    english: 'Malay'
  },
  th: {
    native: 'ไทย',
    english: 'Thai'
  },
  vi: {
    native: 'Tiếng Việt',
    english: 'Vietnamese'
  },
  fil: {
    native: 'Filipino',
    english: 'Filipino'
  },
  km: {
    native: 'ខ្មែរ',
    english: 'Khmer'
  },
  lo: {
    native: 'ລາວ',
    english: 'Lao'
  },
  my: {
    native: 'မြန်မာ',
    english: 'Burmese'
  }
};
export function LanguageSwitcher() {
  const {
    language,
    changeLanguage,
    supportedLanguages
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);
  const filteredLanguages = supportedLanguages.filter(lang => {
    const name = LANGUAGE_NAMES[lang];
    const query = searchQuery.toLowerCase();
    return name.native.toLowerCase().includes(query) || name.english.toLowerCase().includes(query) || lang.toLowerCase().includes(query);
  });
  return <div className="relative" ref={dropdownRef} data-id="element-185">
      <button onClick={() => setIsOpen(!isOpen)} className={`
          flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 border
          ${isOpen ? 'bg-white/20 text-white border-white/30' : 'bg-transparent text-slate-100 border-transparent hover:bg-white/10 hover:text-white'}
        `} aria-expanded={isOpen} aria-haspopup="listbox" aria-label="Select language" data-id="element-186">
        <span className="text-lg leading-none" data-id="element-187">{LANGUAGE_FLAGS[language]}</span>
        <span className="hidden sm:inline font-medium text-sm" data-id="element-188">
          {LANGUAGE_NAMES[language].native}
        </span>
        <span className="sm:hidden font-bold text-xs uppercase" data-id="element-189">
          {language}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-190" />
      </button>

      {isOpen && <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden animate-scale-in origin-top-right" data-id="element-191">
          {/* Header & Search */}
          <div className="p-3 border-b border-slate-100 bg-slate-50" data-id="element-192">
            <div className="relative" data-id="element-193">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-194" />
              <input ref={searchInputRef} type="text" placeholder="Search language..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" data-id="element-195" />
            </div>
          </div>

          {/* Language List */}
          <ul role="listbox" className="max-h-[320px] overflow-y-auto py-1" data-id="element-196">
            {filteredLanguages.length > 0 ? filteredLanguages.map(lang => <li key={lang} data-id="element-197">
                  <button onClick={() => {
            changeLanguage(lang);
            setIsOpen(false);
          }} className={`
                      w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between group
                      ${language === lang ? 'bg-primary-50' : 'hover:bg-slate-50'}
                    `} role="option" aria-selected={language === lang} data-id="element-198">
                    <div className="flex items-center gap-3" data-id="element-199">
                      <span className="text-xl shadow-sm rounded-sm overflow-hidden" data-id="element-200">
                        {LANGUAGE_FLAGS[lang]}
                      </span>
                      <div className="flex flex-col" data-id="element-201">
                        <span className={`font-medium ${language === lang ? 'text-primary-700' : 'text-slate-900'}`} data-id="element-202">
                          {LANGUAGE_NAMES[lang].native}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-203">
                          {LANGUAGE_NAMES[lang].english}
                        </span>
                      </div>
                    </div>
                    {language === lang && <Check className="h-4 w-4 text-primary-600" data-id="element-204" />}
                  </button>
                </li>) : <li className="px-4 py-8 text-center text-sm text-slate-500" data-id="element-205">
                No languages found
              </li>}
          </ul>

          {/* Footer */}
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 text-xs text-center text-slate-400" data-id="element-206">
            Select your preferred language
          </div>
        </div>}
    </div>;
}